/**
 * The dialog box (spec 012, F-057): the only dialog geometry. Every renderer
 * (SceneRuntime, the editing canvas in 017, the choice panels in 020 and 017)
 * lays a dialog out with `layoutDialogBox`, so the box the learner sees and the
 * box the editor shows agree. Pure: text in, stage-px geometry out.
 */

import type { Dialog } from '../@types';
import { computeDialogTimeline } from '../utils/sceneTiming';

/** The dialog text font; `measure` passed to `layoutDialogBox` measures in it. */
export const DIALOG_FONT = '18px system-ui, sans-serif';

/** Gap between the box and the stage edges (stage px). */
export const DIALOG_MARGIN = 10;
/** Horizontal padding of the text inside the box, each side (stage px). */
export const DIALOG_PADDING_X = 16;
/** Height of one text line (stage px). */
export const DIALOG_LINE_HEIGHT = 24;
/** The box is never shorter than this (one line, as before wrapping). */
export const DIALOG_MIN_HEIGHT = 80;
/** At most this many lines (D-1); a longer text ends the last one with "…". */
export const DIALOG_MAX_LINES = 4;
/** Vertical padding added to the lines' height (16 px top and bottom). */
const DIALOG_PADDING_Y = 32;
const ELLIPSIS = '…';

export type DialogBoxPosition = 'top' | 'bottom';

export interface DialogBoxLayout {
  x: number;
  y: number;
  width: number;
  height: number;
  /**
   * The lines to draw, in order, each fitting `width − 32`; `''` is a blank
   * line between paragraphs. When `cut`, the last one ends in the layout's "…".
   */
  lines: string[];
  lineHeight: number;
  /** The text needed more than 4 lines, so the last line ends in the layout's own "…". */
  cut: boolean;
}

/** One dialog box as drawn, for stacking the choice panel on it (020). */
export interface DialogBoxRect {
  id: string;
  position: DialogBoxPosition;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Lay out a dialog's text in its box. `measure(s)` returns the width of `s`
 * in stage px in `DIALOG_FONT` (the runtime passes `ctx.measureText`).
 *
 * An explicit newline always breaks, and an empty paragraph between two with
 * text is a blank line `''`; otherwise words fill a line while it fits the
 * text area, and a word wider than the text area breaks at the last character
 * that fits. Runs of spaces collapse and each line is trimmed. At most 4
 * lines: the 4th is cut so that it plus "…" fits, and `cut` is true. A bottom
 * box keeps its lower edge 10 px above the stage bottom and grows upward; a
 * top box keeps its upper edge at 10 and grows downward.
 *
 * Bounded cost: breaking stops once a 5th line is known to exist, and the
 * break point inside a too-wide word and the cut point are binary searches,
 * giving the same lines as a one-character-at-a-time search.
 */
export function layoutDialogBox(
  text: string,
  position: DialogBoxPosition,
  stage: { width: number; height: number },
  measure: (s: string) => number,
): DialogBoxLayout {
  const width = stage.width - 2 * DIALOG_MARGIN;
  const textWidth = width - 2 * DIALOG_PADDING_X;
  const { lines, more } = breakLines(text ?? '', textWidth, measure);
  if (more) lines[DIALOG_MAX_LINES - 1] = withEllipsis(lines[DIALOG_MAX_LINES - 1], textWidth, measure);
  const height = Math.max(DIALOG_MIN_HEIGHT, lines.length * DIALOG_LINE_HEIGHT + DIALOG_PADDING_Y);
  const y = position === 'top' ? DIALOG_MARGIN : stage.height - DIALOG_MARGIN - height;
  return { x: DIALOG_MARGIN, y, width, height, lines, lineHeight: DIALOG_LINE_HEIGHT, cut: more };
}

/** Memoised `layoutDialogBox` for one measurer (spec 012). */
export interface DialogLayoutCache {
  /**
   * `layoutDialogBox(text, position, stage, measure)`, memoised per (text,
   * position, stage width, stage height, font): a repeat returns the same
   * (shared, never mutated) layout object without measuring.
   */
  layout(
    text: string,
    position: DialogBoxPosition,
    stage: { width: number; height: number },
    measure: (s: string) => number,
    font?: string,
  ): DialogBoxLayout;
}

/** Layouts a cache keeps; the least recently used is dropped past this. */
const DIALOG_LAYOUT_CACHE_SIZE = 64;

/**
 * A dialog layout cache. It belongs to one measurer: every renderer keeps its
 * own (`SceneRuntime` one per instance, the editing canvas one per measurer).
 */
export function createDialogLayoutCache(): DialogLayoutCache {
  const entries = new Map<string, DialogBoxLayout>();
  return {
    layout(text, position, stage, measure, font = DIALOG_FONT) {
      const key = JSON.stringify([text ?? '', position, stage.width, stage.height, font]);
      const hit = entries.get(key);
      if (hit) {
        entries.delete(key);
        entries.set(key, hit);
        return hit;
      }
      const made = layoutDialogBox(text, position, stage, measure);
      Object.freeze(made.lines);
      Object.freeze(made);
      entries.set(key, made);
      if (entries.size > DIALOG_LAYOUT_CACHE_SIZE) entries.delete(entries.keys().next().value as string);
      return made;
    },
  };
}

/**
 * The part of a layout's lines that `visibleText` shows, for the type-in
 * (007): `visibleText` is a prefix of the text the layout was made from, and
 * the result holds the matching prefix of `lines`: complete lines (blank ones
 * included), then at most one partial line. Characters are matched by counting
 * non-space characters, so collapsed spaces, line breaks and blank lines do
 * not shift the reveal. When `cut` (the layout's own), the last line's "…"
 * shows once all its other characters are visible; an authored "…" is a
 * character like any other.
 */
export function visibleDialogLines(lines: readonly string[], visibleText: string, cut: boolean): string[] {
  let remaining = Array.from(visibleText ?? '').filter((c) => !/\s/.test(c)).length;
  const out: string[] = [];
  lines.forEach((line, i) => {
    if (remaining <= 0 || out.length < i) return;
    const chars = Array.from(line);
    const last = i === lines.length - 1;
    const count = cut && last && line.endsWith(ELLIPSIS) ? chars.length - 1 : chars.length;
    let shown = 0;
    let end = 0;
    while (end < count && shown < remaining) {
      if (!/\s/.test(chars[end])) shown++;
      end++;
    }
    remaining -= shown;
    out.push(end >= count ? line : chars.slice(0, end).join('').trimEnd());
    if (end < count) remaining = 0;
  });
  return out;
}

/**
 * The text a dialog's box is laid out from at `tMs`: the current line's whole
 * text (`lineId`, as `FrameState` reports it), so typing never resizes the box.
 * While the panel shows no line (fading in, between lines, fading out) the box
 * keeps the size of the line that shows next, else of the last line shown, so
 * it does not jump when a line starts or ends.
 */
export function dialogBoxText(dialog: Dialog, lineId: string | null, tMs: number): string {
  const lines = dialog.lines ?? [];
  if (lineId !== null) return lines.find((l) => l.id === lineId)?.text ?? '';
  const timeline = computeDialogTimeline(dialog);
  let next = -1;
  let prev = -1;
  timeline.lines.forEach((t, i) => {
    if (t.startMs > tMs && (next < 0 || t.startMs < timeline.lines[next].startMs)) next = i;
    if (t.endMs <= tMs && (prev < 0 || t.endMs >= timeline.lines[prev].endMs)) prev = i;
  });
  const i = next >= 0 ? next : prev;
  return i >= 0 ? lines[i]?.text ?? '' : '';
}

/**
 * Break `text` into at most 4 lines; `more` is true when a 5th line exists
 * (breaking stops there, so the text past it is never measured).
 */
function breakLines(
  text: string,
  maxWidth: number,
  measure: (s: string) => number,
): { lines: string[]; more: boolean } {
  const lines: string[] = [];
  // Blank paragraphs seen since the last line with text; kept only when text follows.
  let blanks = 0;
  const full = () => lines.length >= DIALOG_MAX_LINES;
  /** Adds a line with text (after the pending blank lines); false when the cap is passed. */
  const push = (line: string): boolean => {
    for (; blanks > 0; blanks--) {
      if (full()) return false;
      lines.push('');
    }
    if (full()) return false;
    lines.push(line);
    return true;
  };
  const fits = (s: string) => measure(s) <= maxWidth;
  for (const paragraph of text.replace(/\r\n?/g, '\n').split('\n')) {
    const words = paragraph.split(/\s+/).filter((w) => w.length > 0);
    if (words.length === 0) {
      // Blank lines before the first text are dropped.
      if (lines.length > 0) blanks++;
      continue;
    }
    let current = '';
    for (const word of words) {
      if (full() && !current) return { lines, more: true };
      const candidate = current ? `${current} ${word}` : word;
      if (fits(candidate)) {
        current = candidate;
        continue;
      }
      // With no current line the candidate was the word itself, so it is too wide.
      let restFits = current ? fits(word) : false;
      if (current && !push(current)) return { lines, more: true };
      current = '';
      // A word wider than the text area breaks at the last character that fits.
      let rest = Array.from(word);
      while (!restFits) {
        const n = fitCount(rest, maxWidth, measure);
        if (!push(rest.slice(0, n).join(''))) return { lines, more: true };
        rest = rest.slice(n);
        if (rest.length === 0) break;
        restFits = fits(rest.join(''));
      }
      current = rest.join('');
      if (current && full()) return { lines, more: true };
    }
    if (current && !push(current)) return { lines, more: true };
  }
  return { lines, more: false };
}

/**
 * How many leading characters of `chars` fit `maxWidth` (at least 1), found by
 * binary search: a prefix's width grows with its length. The whole of `chars`
 * is known not to fit.
 */
function fitCount(chars: readonly string[], maxWidth: number, measure: (s: string) => number): number {
  let lo = 1;
  let hi = chars.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (measure(chars.slice(0, mid).join('')) <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** The longest prefix of `line` that, trimmed and followed by "…", fits (binary search). */
function withEllipsis(line: string, maxWidth: number, measure: (s: string) => number): string {
  const chars = Array.from(line);
  const cutAt = (k: number) => chars.slice(0, k).join('').trimEnd() + ELLIPSIS;
  let lo = 0;
  let hi = chars.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (measure(cutAt(mid)) <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return cutAt(lo);
}
